import { PlusIcon } from '@heroicons/react/20/solid';
import { useCallback, useMemo } from 'react';
import { useDropzone } from 'react-dropzone';
import { useCreateListing } from '../_context';
import useMultiImageUpload from '@util/hooks/useMultiImageUpload';
import SafeImage from '@ui/SafeImage';
import { DeletePhotoIcon } from '@c/listing/icons/DeletePhotoIcon';
import { ProductDocument } from 'models/product';
import { cx } from 'class-variance-authority';
import Spinner from '@ui/Spinner';

export default function MobilePhotoSelect() {
  const { form, product } = useCreateListing();

  const { uploadingImage, uploadImages, deleteImage } = useMultiImageUpload({
    id: product.id,
    onUploadCallback: ({ paths, errors }) => {
      const newPaths = paths.slice(0, 10 - product.images.length);
      form.setValue('images', [...product.images, ...newPaths]);

      if (errors.length) {
        form.setError('images', {
          type: 'manual',
          message: errors.join(', '),
        });
      }
    },
    onDeleteCallback: (paths) => form.setValue('images', paths),
    type: 'listing',
  });

  const isUploadDisabled = useMemo(
    () => product.images.length >= 10 || uploadingImage,
    [product.images.length, uploadingImage]
  );

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const isImageFile = acceptedFiles[0]?.type.includes('image');

      if (!isImageFile) {
        form.setError('images', {
          type: 'manual',
          message: 'Only images are allowed',
        });
        return;
      }

      const filesToUpload = acceptedFiles.slice(0, 10 - product.images.length);

      uploadImages(filesToUpload).then(({ paths, errors }) => {
        if (paths.length) {
          form.setValue('images', [...product.images, ...paths]);
          form.trigger('images'); // Trigger form update
        }

        if (errors.length) {
          form.setError('images', {
            type: 'manual',
            message: errors.join(', '),
          });
        }
      });
    },
    [form, product.images, uploadImages]
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    disabled: isUploadDisabled,
  });

  return (
    <div className="flex w-full flex-row gap-4 overflow-x-auto scrollbar-none">
      <AddPhotoBlock
        getRootProps={getRootProps}
        getInputProps={getInputProps}
        isUploadDisabled={isUploadDisabled}
        uploadingImage={uploadingImage}
      />

      {product.images.map((image, index) => (
        <PhotoBlock
          key={image?.thumb ?? 'key-' + index}
          image={image}
          index={index}
          deleteImage={() => deleteImage(image, product.images)}
          onMainImageSelect={(paths) => {
            form.setValue('images', paths);
            form.trigger('images'); // Trigger form update
          }}
          images={product.images}
        />
      ))}

    </div>
  );
}

const AddPhotoBlock = ({
  getRootProps,
  getInputProps,
  isUploadDisabled,
  uploadingImage,
}: {
  getRootProps: () => any;
  getInputProps: () => any;
  isUploadDisabled: boolean;
  uploadingImage: boolean;
}) => {
  return (
    <div
      {...getRootProps()}
      className={cx(
        'relative flex size-[100px] shrink-0 items-center justify-center rounded-[2rem] bg-[#F6F6F6]',
        isUploadDisabled ? 'cursor-default opacity-50' : 'cursor-pointer'
      )}
    >
      <input
        {...getInputProps()}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        disabled={isUploadDisabled}
      />

      <div className="absolute bottom-4 right-4 flex size-12 items-center justify-center rounded-full bg-black">
        <PlusIcon className="h-7 w-7 text-white" />
      </div>

      {uploadingImage ? (
        <Spinner className="size-12" />
      ) : (
        <ImageIcon className="size-12" />
      )}
    </div>
  );
};

const PhotoBlock = ({
  image,
  index,
  deleteImage,
  onMainImageSelect,
  images,
}: {
  image: ProductDocument['images'][0];
  index: number;
  deleteImage: () => void;
  onMainImageSelect: (paths: ProductDocument['images'][0][]) => void;
  images: ProductDocument['images'][0][];
}) => {
  return (
    <div
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData('text/plain', index.toString());
      }}
      onDragOver={(e) => {
        e.preventDefault();
      }}
      onDrop={(e) => {
        const dragIndex = parseInt(e.dataTransfer.getData('text/plain'), 10);
        const dropIndex = index;
        const newImages = [...images];
        const draggedImage = newImages[dragIndex];
        newImages.splice(dragIndex, 1);
        newImages.splice(dropIndex, 0, draggedImage);
        onMainImageSelect(newImages);
      }}
      onClick={() => {
        const newImages = [...images];
        const draggedImage = newImages[index];
        if (!draggedImage) return;
        newImages.splice(index, 1);
        newImages.splice(0, 0, draggedImage);
        onMainImageSelect(newImages);
      }}
      className={cx([
        'relative aspect-square size-[100px] shrink-0 cursor-pointer overflow-hidden rounded-[2rem] shadow-md',
        index === 0 ? 'border-2 border-brand-secondary' : '',
      ])}
    >
      <SafeImage
        alt="product image"
        className="rounded-2xl object-cover"
        src={image.download_url}
        fallbackSrc={image.thumb}
        fill
      />
      <button
        type="button"
        className="absolute right-[.6rem] top-[.6rem] z-20 flex flex-col items-center justify-center"
        onClick={(e) => {
          e.stopPropagation();
          deleteImage();
        }}
      >
        <DeletePhotoIcon height={20} width={20} />
      </button>
    </div>
  );
};

const ImageIcon = ({ className }: { className?: string }) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      className={className}
    >
      <path
        d="M19.5 3.75H4.5C3.25736 3.75 2.25 4.75736 2.25 6V18C2.25 19.2426 3.25736 20.25 4.5 20.25H19.5C20.7426 20.25 21.75 19.2426 21.75 18V6C21.75 4.75736 20.7426 3.75 19.5 3.75Z"
        stroke="#7F7F7F"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <path
        d="M15.75 9.75C16.5784 9.75 17.25 9.07843 17.25 8.25C17.25 7.42157 16.5784 6.75 15.75 6.75C14.9216 6.75 14.25 7.42157 14.25 8.25C14.25 9.07843 14.9216 9.75 15.75 9.75Z"
        stroke="#7F7F7F"
        strokeWidth="1.5"
        strokeMiterlimit="10"
      />
      <path
        d="M14.25 15.7395L10.0003 11.4978C9.72989 11.2275 9.36642 11.0706 8.9842 11.0593C8.60198 11.0479 8.22986 11.183 7.94391 11.4369L2.25 16.4994"
        stroke="#7F7F7F"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M10.5 20.2493L16.2816 14.4677C16.546 14.2028 16.8999 14.0463 17.2738 14.029C17.6477 14.0116 18.0145 14.1347 18.3023 14.374L21.75 17.2493"
        stroke="#7F7F7F"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};
